import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { SectionHead } from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const PLANS = [
    {
        name: "STARTER",
        price: "₹4,999",
        label: "Perfect for Small Businesses",
        features: ["360° Virtual Tour", "Professional 360° Capture", "Basic Navigation", "Website Embed", "Shareable Tour Link", "Mobile Friendly"],
        button: "Get Started",
        popular: false,
    },
    {
        name: "BUSINESS",
        price: "₹9,999",
        label: "For Growing Businesses",
        features: ["Multiple 360° Scenes", "Interactive Navigation", "Google Integration Support", "Website Integration", "Custom Branding", "QR Code", "Shareable Experience"],
        button: "Build My Tour",
        popular: true,
    },
    {
        name: "PROFESSIONAL",
        price: "₹19,999",
        label: "For Premium Spaces",
        features: ["Advanced 360° Experience", "Multiple Areas", "Interactive Hotspots", "Custom Branding", "Website Integration", "Google Support", "QR Code", "Priority Delivery"],
        button: "Go Professional",
        popular: false,
    },
    {
        name: "CUSTOM EXPERIENCE",
        price: "₹25,000+",
        label: "For Large & Customized Projects",
        features: ["Hotels & Resorts", "Large Showrooms", "Schools & Campuses", "Hospitals", "Factories", "Real Estate Projects", "Multi-location Businesses", "Fully Customized Experience"],
        button: "Request Custom Quote",
        popular: false,
    },
];

export default function Pricing() {
    const choose = (plan) => {
        window.dispatchEvent(new CustomEvent("select-plan", { detail: plan }));
        scrollToId("contact");
    };

    return (
        <section id="pricing" className="relative py-20 lg:py-32" data-testid="pricing-section">
            <div className="absolute right-0 top-24 h-80 w-80 rounded-full bg-[#0072FF]/10 blur-[130px]" />
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHead
                    chapter="09"
                    eyebrow="Pricing"
                    title="CHOOSE YOUR 360° EXPERIENCE"
                    sub="One honest price, sized to your space. Pick a plan — we handle the cameras, the stitching and all the tech."
                    align="center"
                />
                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
                    {PLANS.map((p, i) => (
                        <motion.div
                            key={p.name}
                            data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, delay: i * 0.08 }}
                            whileHover={{ y: -10 }}
                            className={`relative flex flex-col rounded-2xl p-8 ${
                                p.popular
                                    ? "border border-[#00C6FF] bg-[#0A1F44]/70 shadow-[0_0_45px_rgba(0,198,255,0.25)]"
                                    : "glass-card"
                            }`}
                        >
                            {p.popular && (
                                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#00C6FF] px-4 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#050B14]">
                                    Most Popular
                                </span>
                            )}
                            <p className="font-mono-accent text-xs tracking-[0.3em] text-[#00C6FF]">
                                {p.name}
                            </p>
                            <p className="mt-4 text-4xl font-extrabold text-white">{p.price}</p>
                            <p className="mt-1.5 text-xs uppercase tracking-[0.15em] text-slate-400">
                                {p.label}
                            </p>
                            <ul className="mt-7 flex-1 space-y-3">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                                        <Check size={15} className="mt-0.5 flex-shrink-0 text-[#00C6FF]" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <button
                                data-testid={`pricing-btn-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => choose(p.name)}
                                className={`mt-8 rounded-full py-3.5 text-xs font-bold uppercase tracking-[0.15em] transition-all duration-300 ${
                                    p.popular
                                        ? "cta-glow bg-[#00C6FF] text-[#050B14] hover:scale-[1.03]"
                                        : "border border-[#00C6FF]/50 text-[#00C6FF] hover:bg-[#00C6FF]/10"
                                }`}
                            >
                                {p.button}
                            </button>
                        </motion.div>
                    ))}
                </div>
                <p className="mt-10 text-center text-xs text-slate-500" data-testid="pricing-note">
                    Final pricing depends on space size, number of locations, number of 360° scenes and customization requirements.
                </p>
            </div>
        </section>
    );
}
